"use client";
import React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { FaExclamationTriangle } from "react-icons/fa";
import theme from "../../../theme";
import api from "../../../api/axiosInstance";

interface RejectionFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  initialRejection: boolean;
  onSubmit: (remarks: string, initialRejection: boolean) => void;
  applicationId: number;
}

export default function RejectionFeedbackModal({
  open,
  onClose,
  initialRejection,
  onSubmit,
  applicationId,
}: RejectionFeedbackModalProps) {
  const [remarks, setRemarks] = useState("");
  const [sendInEmail, setSendInEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async () => {
    if (!remarks.trim()) {
      setSnackbar({
        open: true,
        message: "Please provide rejection remarks",
        severity: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        scholarship_application_id: applicationId,
        is_approved: false,
        remarks: remarks,
        send_rejection_reason_in_email: sendInEmail,
      };

      await api.post(
        initialRejection
          ? "/scholarship-applications/initial-admin-approval/"
          : "/scholarship-applications/final-admin-approval/",
        payload
      );

      setSnackbar({
        open: true,
        message: `Application ${initialRejection ? 'initially' : 'finally'} rejected successfully`,
        severity: "success",
      });

      // Reset form and close modal after successful submission
      setRemarks("");
      setTimeout(() => {
        onSubmit(remarks, initialRejection);
      }, 1000);
    } catch (error: any) {
      console.error("Error rejecting application:", error);
      
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.detail || 
                          "Failed to reject application. Please try again.";
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setRemarks("");
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            maxWidth: "500px",
            width: "100%",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            bgcolor: "#d32f2f",
            color: "white",
            display: "flex",
            alignItems: "center",
          }}
        >
          <FaExclamationTriangle style={{ marginRight: "10px" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Rejection Remarks
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 3, pt: 2 }}>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Please provide a reason for rejecting this scholarship application.
              This feedback will be recorded and may be shared with the applicant.
            </Typography>

            <TextField
              label="Rejection Remarks"
              placeholder="Enter detailed remarks explaining why this application is being rejected..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: "6px",
                  fontSize: "14px",
                },
              }}
              required
              error={remarks.trim() === ""}
              helperText={remarks.trim() === "" ? "Remarks are required" : ""}
              sx={{ mb: 2 }}
              disabled={loading}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={sendInEmail}
                  onChange={(e) => setSendInEmail(e.target.checked)}
                  color="error"
                  disabled={loading}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Send rejection reason in email
                </Typography>
              }
              sx={{ mb: 1, ml: 0 }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={loading}
                sx={{
                  mr: 2,
                  color: theme.palette.colors["white"]["400"],
                  backgroundColor: theme.palette.colors.black["600"],
                  borderColor: "#5a5a5a",
                  "&:hover": {
                    borderColor: "#3a3a3a",
                    backgroundColor: theme.palette.colors.gray["800"],
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!remarks.trim() || loading}
                sx={{
                  bgcolor: "#d32f2f",
                  "&:hover": {
                    bgcolor: "#b71c1c",
                  },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={16}
                      style={{
                        marginRight: 8,
                        color: "white",
                      }}
                    />
                    Submitting...
                  </>
                ) : (
                  "Submit Rejection"
                )}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
